import { PlusIcon } from "lucide-react";
import { Account } from "../graphql/generated";
import { Avatar } from "./account/Avatar";
import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";

interface Props {
  accounts: Pick<Account, "id" | "name" | "avatar">[];
  max?: number;
  size?: "xs" | "s" | "m" | "l" | "xl" | "xxl";
  onClick?: () => void;
  showAdd?: boolean;
}

const sizesMap = {
  xs: "w-4 h-4",
  s: "w-5 h-5",
  m: "w-6 h-6",
  l: "w-8 h-8",
  xl: "w-10 h-10",
  xxl: "w-12 h-12",
};

const iconSizesMap = {
  xs: "w-2 h-2",
  s: "w-3 h-3",
  m: "w-4 h-4",
  l: "w-5 h-5",
  xl: "w-6 h-6",
  xxl: "w-8 h-8",
};

export function Avatars(props: Props) {
  const accountsToShow = props.accounts.slice(0, props.max);
  const extraAccounts = props.max ? props.accounts.length - props.max : 0;
  const hiddenAccounts = props.max ? props.accounts.slice(props.max) : [];

  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (showTooltip && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setTooltipPosition({
        top: rect.top - 10,
        left: rect.left + rect.width / 2,
      });
    }
  }, [showTooltip]);

  return (
    <div
      className="flex -space-x-1 group cursor-pointer hover:opacity-90"
      onClick={props.onClick}
    >
      {accountsToShow.map((ac) => {
        return (
          <Avatar
            key={ac.id}
            account={ac}
            size={props.size ? props.size : "xs"}
          />
        );
      })}
      {extraAccounts > 0 ? (
        <div
          ref={triggerRef}
          className={clsx(
            "flex-shrink-0 ring-concrete-100 ring-1 bg-concrete-50 rounded-full flex items-center justify-center z-30 cursor-pointer hover:bg-concrete-100 transition-colors",
            props.size ? sizesMap[props.size] : sizesMap["xs"],
          )}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          <p className="text-xs text-concrete-400">+{extraAccounts}</p>
        </div>
      ) : null}
      {props.showAdd ? (
        <div
          ref={triggerRef}
          className={clsx(
            "flex-shrink-0 ring-concrete-100 ring-1 bg-concrete-50 rounded-full flex items-center justify-center z-30 cursor-pointer group-hover:bg-concrete-100 transition-colors",
            props.size ? sizesMap[props.size] : sizesMap["xs"],
          )}
        >
          <PlusIcon
            className={clsx(
              props.size ? iconSizesMap[props.size] : iconSizesMap["xs"],
              "text-concrete-400",
            )}
          />
        </div>
      ) : null}

      {showTooltip &&
        extraAccounts > 0 &&
        createPortal(
          <div
            className="fixed z-[9999] pointer-events-none"
            style={{
              top: `${tooltipPosition.top}px`,
              left: `${tooltipPosition.left}px`,
              transform: "translate(-50%, -100%)",
            }}
          >
            <div
              className="py-3 px-4 rounded-lg border border-gray-200 bg-white shadow-lg relative max-w-sm
              before:absolute before:left-1/2 before:bottom-0 before:-mb-[8px] 
              before:border-transparent before:border-t-gray-200 before:border-t-[8px] 
              before:border-x-[8px] before:border-b-0 before:content-[''] before:-translate-x-1/2"
            >
              <div className="space-y-2">
                <p className="text-sm font-semibold text-gray-900 mb-2">
                  {extraAccounts} more{" "}
                  {extraAccounts === 1 ? "person" : "people"}
                </p>
                <div className="grid grid-cols-3 gap-x-2 gap-y-1">
                  {hiddenAccounts.map((account) => (
                    <div
                      key={account.id}
                      className="flex items-center space-x-1.5 min-w-0"
                    >
                      <Avatar account={account} size="xs" />
                      <span className="text-xs text-gray-700 truncate">
                        {account.name}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
